<script setup>
import {computed, ref, watch} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {useAuthStore} from "@/stores/auth";
import CopyrightComponent from "@/components/utils/CopyrightComponent.vue";
import {getUserRole} from "@/utils/roleHelpers";
import { APP_NAME } from "@/globals/contants";
import apiClient from "@/api/axios";

const authStore = useAuthStore();

// Estado para manejar la visibilidad de la barra lateral en pantallas pequeñas
const isSidebarOpen = ref(false);

const user = authStore.getUser;

// obtener el nombre de la ruta actual
const currentRoute = useRoute();

// Función para alternar la barra lateral
const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
};

// Función para manejar el cierre de sesión
const router = useRouter();
const handleLogout = async () => {
  await router.push('/logout');
};

// Comprobacion cada 1 minuto de que esta autenticado o el token no ha expirado
setInterval(async () => {
  try {
    // Realizamos una petición al endpoint de verificación del token
    await apiClient.get('/auth/verify-token');
  } catch (error) {
    // Si la verificación falla, eliminamos los datos almacenados y redirigimos al login
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    await router.push('/login');
  }
}, 60000);

const allMenuItems = [
  { name: "Dashboard", path: user.role === 'user' ? '/user/dashboard' : '/dashboard', roles: ["admin", "user"] },
  { name: "Taxi", path: "/cars", roles: ["admin"] },
  { name: "Users", path: "/users", roles: ["admin"] },
  { name: "Drivers", path: "/drivers", roles: ["admin"] },
  { name: "Trips", path: user.role === 'user' ? '/user/trips' : '/trips', roles: ["admin", "user"] },
  { name: "Profile", path: "/profile", roles: ["admin", "user"] },
  { name: "About", path: "/about", roles: ["admin", "user"] },
];

// Filtrar menús según el rol
const menuItems = computed(() => {
  return allMenuItems.filter((item) => item.roles.includes(getUserRole()));
});

// Buscador (Funcionalidad pendiente)
// const searchQuery = ref('');

// Cerrar la barra lateral al cambiar de ruta en pantallas pequeñas
const route = useRoute();
watch(route, () => {
  if (window.innerWidth < 768) {
    isSidebarOpen.value = false;
  }
});
</script>

<template>
  <div class="flex h-screen bg-gray-100 dark:bg-gray-800">

    <!-- Overlay -->
    <div
        v-if="isSidebarOpen"
        class="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
        @click="toggleSidebar"
    ></div>

    <!-- Barra Lateral -->
    <aside
        :class="[
        'fixed inset-y-0 left-0 z-40 w-64 bg-white dark:bg-gray-900 shadow-lg transform transition-transform duration-300 ease-in-out z-40',
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
        'md:static md:inset-0 md:translate-x-0'
      ]"
    >
      <div class="flex items-center justify-center h-16 shadow-md">
        <h1 class="text-xl font-bold text-emerald-900"><img src="/images/electride_app-icon.svg" alt="{{APP_NAME}}" width="50" class="inline-block" />
          <span class="text-emerald-900 dark:text-emerald-600">{{APP_NAME}}</span></h1>
        <button @click="toggleSidebar" class="text-gray-950 dark:text-gray-50 focus:outline-none md:hidden">
          <!-- Icono de menú (Hamburger) -->
          <svg class="w-6 h-6 text-black dark:text-white ml-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
      <nav class="mt-2">
        <router-link
            v-for="item in menuItems"
            :key="item.name"
            :to="item.path"
            :exact="item.path === '/'"
            class="flex items-center px-4 py-2 mt-2 text-gray-600 dark:text-gray-400 hover:bg-emerald-400 hover:text-black transition-colors duration-200"
            exact-active-class="bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-100"
        >
          <span class="mx-4">{{ item.name }}</span>
        </router-link>
      </nav>
    </aside>

    <!-- Contenedor Principal -->
    <div class="flex flex-col flex-1 w-full">
      <!-- Encabezado -->
      <header class="flex items-center justify-between px-6 py-4 bg-gray-100 dark:bg-emerald-600 shadow-md">
        <div class="flex items-center">
          <!-- Botón de Toggle solo visible en pantallas pequeñas -->
          <button @click="toggleSidebar" class="text-gray-500 focus:outline-none md:hidden">
            <!-- Icono de menú (Hamburger) -->
            <svg class="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h3 class="ml-4 text-2xl font-semibold text-emerald-700 dark:text-emerald-800 ">
            {{ menuItems.find(item => item.path === $route.path)?.name || currentRoute.name }}
          </h3>
        </div>
        <div class="flex items-center space-x-4">
          <!--          &lt;!&ndash; Buscador &ndash;&gt;-->
          <!--          <div class="relative">-->
          <!--            <input-->
          <!--                type="text"-->
          <!--                placeholder="Buscar..."-->
          <!--                v-model="searchQuery"-->
          <!--                class="hidden md:block w-60 px-1 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ease-in-out"-->
          <!--            />-->
          <!--            <svg-->
          <!--                v-if="searchQuery === ''"-->
          <!--                class="hidden md:block absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"-->
          <!--                fill="none"-->
          <!--                stroke="currentColor"-->
          <!--                viewBox="0 0 24 24"-->
          <!--            >-->
          <!--              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M18 11a7 7 0 11-14 0 7 7 0 0114 0z" />-->
          <!--            </svg>-->
          <!--          </div>-->

          <!--          &lt;!&ndash; Iconos de notificaciones y perfil &ndash;&gt;-->
          <!--          <button class="relative focus:outline-none">-->
          <!--            <IconComponent icon="bell" :size="25" />-->
          <!--            &lt;!&ndash; Indicador de notificaciones &ndash;&gt;-->
          <!--            <span class="absolute top-0 right-0 inline-block w-2 h-2 bg-red-600 rounded-full"></span>-->
          <!--          </button>-->
          <!--          visible only lg-->
          <div class="inline-block mx-5 hidden lg:inline-block">
            <router-link
                :to="'/profile/'"
                class="flex items-center text-emerald-700 hover:text-emerald-800 dark:text-emerald-800 dark:hover:text-emerald-900 font-semibold transition-colors duration-200"
            >
              <span class="material-icons-round">account_circle</span>
              {{user.name}}
            </router-link>

          </div>
          <button @click="handleLogout" class="text-emerald-700 hover:text-emerald-800 dark:text-emerald-800 dark:hover:text-emerald-900 focus:outline-none">
            <span class="material-icons-round">power_settings_new</span>
          </button>
        </div>
      </header>

      <!-- Área de Contenido -->
      <main class="flex-1 p-6 overflow-y-auto">
        <div class="flex flex-col" style="min-height: 86vh;">
          <!-- Content -->
          <div class="flex-grow">
            <router-view />
          </div>

          <!-- Footer -->
          <footer class="bg-gray-50 dark:bg-gray-700 text-gray-400 dark:text-gray-500 text-center py-6 rounded mt-3">
            <CopyrightComponent />
          </footer>
        </div>
      </main>
    </div>
  </div>

</template>

<style scoped>
/* Animaciones para la barra lateral */
.transition-transform {
  transition: transform 0.3s ease-in-out;
}

/* Buscador: icono de búsqueda */
input[type='text']::placeholder {
  color: #a0aec0; /* Tailwind color gray-400 */
}

/* Media Queries para ajustar el diseño */
@media (max-width: 767px) {
  /* Asegurarse de que el buscador esté oculto en pantallas pequeñas */
  input[type='text'] {
    display: none;
  }
}

/* Estilos adicionales si es necesario */
</style>
